import { FC } from 'react';

import { AvailableCurrencies, CURRENCY_SYMBOLS, formatCostInCents } from 'utils/clickme';
import { NON_BREAKING_SPACE } from 'utils/constants/common';
import { Settings } from 'utils/constants/dynamicSettings';

import { ElementShownAnchor } from '@hh.ru/analytics-js';
import auctionCampaignIncreaseBudgetAdviceButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/auction/auction_campaign_increase_budget_advice_button_click';
import auctionCampaignIncreaseBudgetAdviceElementShown from '@hh.ru/analytics-js-events/build/clickme/employer/auction/auction_campaign_increase_budget_advice_element_shown';
import { VSpacing } from '@hh.ru/magritte-ui';

import { useAuctionBidValidation } from 'hooks/auction/useAuctionBidValidation';
import { useAuctionBudgetValidation } from 'hooks/auction/useAuctionBudgetValidation';
import useTrls from 'hooks/translation/useTrls';
import useSettings from 'hooks/useSettings';

import { AuctionAutoStrategySettings } from 'components/AuctionBuilder/types/auctionAutoStrategySettings';
import { FormAdvice } from 'components/common/FormAdvice/FormAdvice';

import {
    AuctionDataField,
    auctionSelector,
    auctionSelectorNonNullable,
    BiddingStrategy,
    setBudgetValue,
} from 'store/models/auctionData';
import { useDispatch, useSelector } from 'store/utils';

const TrlKeys = {
    text: 'AuctionBuilder.AuctionBudget.autoStrategyAdvice.text',
    actionButton: 'AuctionBuilder.AuctionBudget.autoStrategyAdvice.actionButton',
};

export const AuctionBudgetAutoStrategyAdvice: FC = () => {
    const dispatch = useDispatch();
    const trls = useTrls();
    const settings = useSettings();
    const validateBudget = useAuctionBudgetValidation();
    const validateBid = useAuctionBidValidation();

    const biddingStrategy = useSelector(auctionSelectorNonNullable[AuctionDataField.BiddingStrategy]);
    const { value: bidValue } = useSelector(auctionSelectorNonNullable.bid);
    const { value: budgetValue } = useSelector(auctionSelectorNonNullable.budget);
    const vacancyId = useSelector(auctionSelector.vacancy)?.vacancyId;

    const { recommendedMinBudgetInCents: recommendedMinBudgetRaw } =
        (settings(Settings.AuctionAutoStrategySettings) as AuctionAutoStrategySettings | null) || {};

    if (!recommendedMinBudgetRaw) {
        return null;
    }

    const recommendedMinBudgetInCents = Number(recommendedMinBudgetRaw);
    const shouldShowAutoStrategyAdvice =
        Number.isFinite(recommendedMinBudgetInCents) &&
        biddingStrategy === BiddingStrategy.Auto &&
        budgetValue !== null &&
        budgetValue < recommendedMinBudgetInCents;

    if (!shouldShowAutoStrategyAdvice) {
        return null;
    }

    const recommendedMinBudgetLabel = `${formatCostInCents(recommendedMinBudgetInCents, NON_BREAKING_SPACE)} ${
        CURRENCY_SYMBOLS[AvailableCurrencies.RUR]
    }`;

    const onApplyRecommendedBudget = () => {
        dispatch(setBudgetValue(recommendedMinBudgetInCents));
        validateBudget(recommendedMinBudgetInCents);
        validateBid(bidValue, recommendedMinBudgetInCents);
        auctionCampaignIncreaseBudgetAdviceButtonClick({
            biddingStrategy,
            vacancyId: String(vacancyId),
        });
    };

    return (
        <ElementShownAnchor
            fn={auctionCampaignIncreaseBudgetAdviceElementShown}
            vacancyId={String(vacancyId)}
            biddingStrategy={biddingStrategy}
        >
            <VSpacing default={16} />
            <FormAdvice
                message={trls(TrlKeys.text)}
                actionLabel={trls(TrlKeys.actionButton, [recommendedMinBudgetLabel])}
                onActionClick={onApplyRecommendedBudget}
                actionDataQa="set-recommended-budget"
            />
        </ElementShownAnchor>
    );
};
